import { ResolveFn } from '@angular/router';
import { inject } from '@angular/core';
import { defer, Observable, of, switchMap, take } from 'rxjs';
import { MenuService } from '../shared/menu.service';
import { ProductsService } from '../product/products.service';
import { SignalsStoreService } from '../shared/signals-store.service';
import { formatStringForURL } from '../shared/common/utils';

/**
 * SSR resolver for /shop, /shop/:category, /shop/:category/:subcategory.
 *
 * Pre-fetches the products resume server-side so the LCP banner image URL is
 * present in the initial HTML. Without this, the browser only discovers the
 * banner URL after Angular hydrates and the client makes the API call —
 * ~1.3s of "resource load delay" in Lighthouse.
 *
 * Returns an Observable so the Router blocks navigation until the products are
 * loaded. On SSR the HTTP response is captured into Angular's TransferCache;
 * on the client the resolver re-runs but the HttpClient call resolves
 * synchronously from cache, so there is no second network round-trip.
 */
export const shopResolver: ResolveFn<boolean> = (route) => {
  const menuService = inject(MenuService);
  const productsService = inject(ProductsService);
  const signalsStore = inject(SignalsStoreService);

  const categorySlug = route.paramMap.get('category');

  // Ensure the menu is loaded — it's the slug → categoryId mapping source.
  // If already populated (subsequent navigations), reuse it; otherwise fetch.
  const menu$: Observable<any[]> = menuService.menu().length
    ? of(menuService.menu())
    : menuService.fetchMenuCategories().pipe(
      // fetchMenuCategories taps the result into `menu` signal, so we read
      // it back here. Use take(1) to complete the observable.
      take(1),
      switchMap(() => of(menuService.menu())),
    );

  return menu$.pipe(
    take(1),
    switchMap((menu) => {
      if (!menu?.length) return of(true);

      // Resolve the category to fetch: matching slug or the first menu entry.
      const category = categorySlug
        ? menu.find((c) => formatStringForURL(c.name) === categorySlug)
        : menu[0];
      if (!category) return of(true);

      // Mirror the side effect of the existing header flow so the rest of the
      // app sees the same selectedCategory state it would expect at this URL.
      signalsStore.selectedCategory.set({ id: category.id, name: category.name });

      // defer() so the HTTP call is only made when subscribed (lets `of(true)`
      // short-circuits above skip the network).
      return defer(() => productsService.getProductsResume$(category.id)).pipe(
        take(1),
        switchMap(() => of(true)),
      );
    }),
    // Never reject navigation — a failed resolve falls back to client-side
    // fetching like before. LCP is opportunistic, not load-blocking.
    switchMap((v) => of(v)),
  );
};
